/**
 * Outreach v2 — branch-tree model + auto-layout (spec §5, §6).
 *
 * Pure, framework-free helpers behind the React Flow editor. The recruiter never
 * hand-wires edges; every mutation here keeps the tree DETERMINISTIC and
 * engine-valid (apps/server/modules/outreach/sequence-engine.service.js):
 *
 *  - linear flow = a single plain edge (`branch: null`) per non-branch node;
 *  - a `branch` node forks into exactly one `yes` and one `no` edge, and its
 *    `branches.{yes,no}` pointers ALWAYS mirror those edges (the engine reads
 *    `node.branches` to navigate; the canvas reads the edges);
 *  - `entryNodeId` is the node with no incoming edge.
 *
 * `normalizeTree` is the invariant-keeper: edges are the source of truth, it
 * re-derives every `branches` map from the edges, recomputes the entry node, and
 * prunes anything no longer reachable. Every mutation funnels through it, so the
 * editor can never persist a tree the engine can't run.
 */

import { iterateAiBlocks } from './ai-blocks.js';
import type {
    ConditionType,
    ConditionTimingMode,
    DelayUnit,
    FallbackType,
    SequenceEdge,
    StepChannel,
    StepNode,
    StepType,
} from './types';

/* ─── tree shape ─── */

export type SequenceTree = {
    nodes: StepNode[];
    edges: SequenceEdge[];
    entryNodeId: string | null;
};

/** The three structural slots a node's outgoing edge can occupy. */
export type SlotLabel = 'yes' | 'no' | null;

/* ─── ids ─── */

export function newNodeId(): string {
    return `n-${Math.random().toString(36).slice(2, 9)}`;
}

function newEdgeId(): string {
    return `e-${Math.random().toString(36).slice(2, 9)}`;
}

function makeEdge(source: string, target: string, branch: SlotLabel): SequenceEdge {
    return { id: newEdgeId(), source, target, branch };
}

/* ─── meta / labels ─── */

export const STEP_TYPE_META: Record<StepType, { label: string; channel: StepChannel | null; action: string }> = {
    email: { label: 'Email', channel: 'email', action: 'Send email' },
    linkedin_invite: { label: 'LinkedIn invite', channel: 'linkedin', action: 'Send connection invite' },
    linkedin_message: { label: 'LinkedIn message', channel: 'linkedin', action: 'Send message' },
    linkedin_inmail: { label: 'LinkedIn InMail', channel: 'linkedin', action: 'Send InMail' },
    whatsapp_message: { label: 'WhatsApp', channel: 'whatsapp', action: 'Send WhatsApp message' },
    manual_task: { label: 'Manual task', channel: null, action: 'Create a task' },
    branch: { label: 'Branch', channel: null, action: 'Branch on a condition' },
};

/** Step types the recruiter can add (everything except the bare `branch`, which
 *  is added via the dedicated "add branch" action). */
export const ADDABLE_STEP_TYPES: StepType[] = [
    'email',
    'linkedin_invite',
    'linkedin_message',
    'linkedin_inmail',
    'whatsapp_message',
    'manual_task',
];

/* ─── AI-generate blocks (§7 constructor) ─── *
 * Client mirror of the server's AI-block detection (message-render.service.js):
 * `{{ai}}` / `{{ai: hint}}` blocks in a step's copy make it an AI step — filled
 * per candidate at send time, sample approval gates launch. There is no mode
 * picker; behavior is derived from content. */

export function hasAiBlocks(text?: string | null): boolean {
    return !iterateAiBlocks(String(text ?? '')).next().done;
}

/** Whether a send node's copy contains any `{{ai}}` blocks — same field rules
 *  as the server (`subject`+`body` for email, `inviteNote` for invites). */
export function nodeHasAiBlocks(node: Partial<StepNode> | null | undefined): boolean {
    if (!node) return false;
    const msg = node.message;
    if (!msg) return false;
    if (node.type === 'email' || node.type === 'linkedin_inmail')
        return hasAiBlocks(msg.subject) || hasAiBlocks(msg.body);
    if (node.type === 'linkedin_invite') return hasAiBlocks(msg.inviteNote ?? msg.body);
    return hasAiBlocks(msg.body);
}

/** The token the "AI generate" button inserts at the cursor. */
export const AI_BLOCK_PLACEHOLDER = '{{ai: describe what to write}}';

export const CONDITION_LABELS: Record<ConditionType, string> = {
    hasEmail: 'has an email address',
    hasLinkedIn: 'has a LinkedIn profile',
    hasPhone: 'has a phone number',
    inviteAccepted: 'accepted the LinkedIn invite',
    repliedAnyChannel: 'replied on any channel',
    emailBounced: 'email bounced',
    unsubscribed: 'unsubscribed',
    taskCompleted: 'task completed',
    opened: 'opened the email',
    clicked: 'clicked a link',
    markedInterested: 'marked interested',
    markedMaybe: 'marked maybe',
    markedNotInterested: 'marked not interested',
};

/** Conditions worth offering in the editor's branch picker. Trimmed to the forks
 *  that actually work + make sense (CONDITION_LABELS stays complete so any existing
 *  branch still renders its label). Hidden for now:
 *    • repliedAnyChannel — the sequence already stops on any reply, so branching on
 *      it is redundant.
 *    • hasLinkedIn / hasPhone / markedInterested / taskCompleted — deferred.
 *    • opened / clicked — open/click tracking is OFF by default (deliverability, §6),
 *      so the engine resolves them to "No" — non-functional until tracking is enabled. */
export const BRANCH_CONDITION_OPTIONS: ConditionType[] = ['inviteAccepted', 'hasEmail'];

export const FALLBACK_LABELS: Record<FallbackType, string> = {
    none: 'No fallback (skip)',
    email: 'Fall back to email',
    linkedin_invite: 'Fall back to LinkedIn invite',
    linkedin_message: 'Fall back to LinkedIn message',
    linkedin_inmail: 'Fall back to LinkedIn InMail',
    whatsapp_message: 'Fall back to WhatsApp message',
    skip: 'Skip this step',
};

export const DELAY_UNITS: DelayUnit[] = ['minutes', 'hours', 'days'];

/** §6: a 0-day "Within" is invalid — mirror the engine's MIN_WITHIN_DAYS floor. */
export const MIN_WITHIN_DAYS = 1;

/* ─── node factory ─── */

function baseNode(type: StepType): StepNode {
    return {
        id: newNodeId(),
        type,
        channel: STEP_TYPE_META[type].channel,
        delay: { value: 0, unit: 'days' },
        requiredFields: [],
        accountSelection: { mode: 'auto', accountId: null },
        message: {
            subject: '',
            body: '',
            inviteNote: '',
            aiPrompt: '',
            personalizationMode: 'light',
            variables: [],
            unsubscribeFooter: false,
            requiresConnected: false,
            fallbackIfNotAccepted: 'email',
        },
        aiMode: 'manual',
        approvalRequired: false,
        condition: null,
        branches: { yes: null, no: null },
        task: null,
        invite: null,
        fallback: { type: 'none', targetNodeId: null },
        position: { x: 0, y: 0 },
    };
}

/**
 * Build an engine-valid node of `type` with the right channel + required fields
 * + per-type sub-objects already populated. `overrides` is shallow-merged.
 */
export function makeNode(type: StepType, overrides: Partial<StepNode> = {}): StepNode {
    const node = baseNode(type);

    switch (type) {
        case 'email':
            node.requiredFields = ['email'];
            break;
        case 'linkedin_invite':
            node.requiredFields = ['linkedinUrl'];
            node.invite = {
                maxWaitForAcceptanceDays: 5,
                fallbackIfMissing: 'email',
                fallbackIfConnected: 'linkedin_message',
                fallbackIfNotAccepted: 'email',
            };
            break;
        case 'linkedin_message':
            node.requiredFields = ['linkedinUrl'];
            node.message.requiresConnected = true;
            node.message.fallbackIfNotAccepted = 'email';
            break;
        case 'linkedin_inmail':
            node.requiredFields = ['linkedinUrl'];
            node.message.requiresConnected = false;
            node.fallback = { type: 'email', targetNodeId: null };
            break;
        case 'whatsapp_message':
            node.requiredFields = ['phone'];
            node.message.unsubscribeFooter = false;
            break;
        case 'manual_task':
            node.task = {
                title: '',
                instructions: '',
                assignedUserId: null,
                dueOffsetDays: 0,
                blocksSequence: false,
            };
            break;
        case 'branch':
            node.condition = { type: 'inviteAccepted', timing: { mode: 'within', windowDays: 5 } };
            break;
    }

    return { ...node, ...overrides };
}

/* ─── default passive-candidate tree (§5) ─── *
 * Email intro (day 0) → LinkedIn invite (+2d) → branch: accepted within 5d?
 *   Yes: LinkedIn message → soft-close email (+3d)
 *   No:  email follow-up  → soft-close email (+3d)
 */
export function defaultPassiveTree(): SequenceTree {
    const intro = makeNode('email', {
        delay: { value: 0, unit: 'days' },
        message: {
            subject: 'Quick note about {{job.title}} at {{company.name}}',
            body: 'Hi {{candidate.firstName}},\n\nI came across your profile and thought the {{job.title}} role at {{company.name}} could be a strong fit. Would you be open to a short conversation?\n\nBest,\n{{recruiter.firstName}}',
            inviteNote: '',
            personalizationMode: 'light',
            variables: [],
            unsubscribeFooter: false,
            requiresConnected: false,
            fallbackIfNotAccepted: 'email',
        },
    });

    const invite = makeNode('linkedin_invite', {
        delay: { value: 2, unit: 'days' },
        message: {
            subject: '',
            body: '',
            inviteNote:
                'Hi {{candidate.firstName}} — I’m hiring for {{job.title}} at {{company.name}} and would love to connect.',
            personalizationMode: 'light',
            variables: [],
            unsubscribeFooter: false,
            requiresConnected: false,
            fallbackIfNotAccepted: 'email',
        },
    });

    const branch = makeNode('branch', {
        delay: { value: 0, unit: 'days' },
        condition: { type: 'inviteAccepted', timing: { mode: 'within', windowDays: 5 } },
    });

    const liMessage = makeNode('linkedin_message', {
        delay: { value: 0, unit: 'days' },
        message: {
            subject: '',
            body: 'Thanks for connecting, {{candidate.firstName}}! Would you be open to a quick chat about the {{job.title}} role at {{company.name}}?',
            inviteNote: '',
            personalizationMode: 'light',
            variables: [],
            unsubscribeFooter: false,
            requiresConnected: true,
            fallbackIfNotAccepted: 'email',
        },
    });

    const softCloseYes = makeNode('email', {
        delay: { value: 3, unit: 'days' },
        message: {
            subject: 'Following up — {{job.title}}',
            body: 'Hi {{candidate.firstName}},\n\nJust wanted to follow up in case the timing is better now. Happy to share more about {{job.title}} whenever works for you.\n\nBest,\n{{recruiter.firstName}}',
            inviteNote: '',
            personalizationMode: 'light',
            variables: [],
            unsubscribeFooter: false,
            requiresConnected: false,
            fallbackIfNotAccepted: 'email',
        },
    });

    const emailFollowup = makeNode('email', {
        delay: { value: 0, unit: 'days' },
        message: {
            subject: 'Re: {{job.title}} at {{company.name}}',
            body: 'Hi {{candidate.firstName}},\n\nCircling back on my note — would you be interested in hearing more about {{job.title}}? Even if the timing isn’t right, happy to stay in touch.\n\nBest,\n{{recruiter.firstName}}',
            inviteNote: '',
            personalizationMode: 'light',
            variables: [],
            unsubscribeFooter: false,
            requiresConnected: false,
            fallbackIfNotAccepted: 'email',
        },
    });

    const softCloseNo = makeNode('email', {
        delay: { value: 3, unit: 'days' },
        message: {
            subject: 'One last note — {{job.title}}',
            body: 'Hi {{candidate.firstName}},\n\nI’ll leave it here for now, but if {{job.title}} at {{company.name}} sounds interesting down the line, just reply and we’ll pick it up.\n\nBest,\n{{recruiter.firstName}}',
            inviteNote: '',
            personalizationMode: 'light',
            variables: [],
            unsubscribeFooter: false,
            requiresConnected: false,
            fallbackIfNotAccepted: 'email',
        },
    });

    const nodes = [intro, invite, branch, liMessage, softCloseYes, emailFollowup, softCloseNo];
    const edges: SequenceEdge[] = [
        makeEdge(intro.id, invite.id, null),
        makeEdge(invite.id, branch.id, null),
        makeEdge(branch.id, liMessage.id, 'yes'),
        makeEdge(branch.id, emailFollowup.id, 'no'),
        makeEdge(liMessage.id, softCloseYes.id, null),
        makeEdge(emailFollowup.id, softCloseNo.id, null),
    ];

    return normalizeTree({ nodes, edges, entryNodeId: intro.id });
}

/* ─── invariants ─── */

/** Outgoing edge of `nodeId` in the given slot, or undefined. */
function edgeInSlot(edges: SequenceEdge[], nodeId: string, slot: SlotLabel): SequenceEdge | undefined {
    return edges.find((e) => e.source === nodeId && (e.branch ?? null) === slot);
}

/** Where the engine goes after `node` when it is removed / collapsed. */
function primarySuccessor(tree: SequenceTree, node: StepNode): string | null {
    if (node.type === 'branch') {
        return edgeInSlot(tree.edges, node.id, 'no')?.target ?? edgeInSlot(tree.edges, node.id, 'yes')?.target ?? null;
    }
    return edgeInSlot(tree.edges, node.id, null)?.target ?? null;
}

function reachableFrom(entry: string, edges: SequenceEdge[]): Set<string> {
    const adj = new Map<string, string[]>();
    for (const e of edges) {
        if (!adj.has(e.source)) adj.set(e.source, []);
        adj.get(e.source)!.push(e.target);
    }
    const seen = new Set<string>();
    const stack = [entry];
    while (stack.length) {
        const id = stack.pop()!;
        if (seen.has(id)) continue;
        seen.add(id);
        for (const t of adj.get(id) ?? []) stack.push(t);
    }
    return seen;
}

/**
 * Re-establish every invariant: drop dangling edges, re-derive each node's
 * `branches` map from its edges (edges win), recompute the entry node, and prune
 * anything unreachable from it. Idempotent — safe to call after every edit and
 * before every persist.
 */
export function normalizeTree(tree: SequenceTree): SequenceTree {
    const nodeIds = new Set(tree.nodes.map((n) => n.id));

    // 1. drop edges to/from nodes that no longer exist + self-loops.
    let edges = tree.edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target) && e.source !== e.target);

    // 2. branch nodes own exactly one yes + one no edge; collapse duplicates.
    const seenSlots = new Set<string>();
    edges = edges.filter((e) => {
        const key = `${e.source}::${e.branch ?? 'next'}`;
        if (seenSlots.has(key)) return false;
        seenSlots.add(key);
        return true;
    });

    // 3. sync branches{} from edges (engine reads node.branches; canvas reads edges).
    const nodes = tree.nodes.map((n) => {
        if (n.type === 'branch') {
            return {
                ...n,
                branches: {
                    yes: edgeInSlot(edges, n.id, 'yes')?.target ?? null,
                    no: edgeInSlot(edges, n.id, 'no')?.target ?? null,
                },
            };
        }
        return { ...n, branches: { yes: null, no: null } };
    });

    // 4. entry = the explicit entry if still valid, else the first node nothing points at.
    let entryNodeId = tree.entryNodeId && nodeIds.has(tree.entryNodeId) ? tree.entryNodeId : null;
    if (!entryNodeId && nodes.length) {
        const targets = new Set(edges.map((e) => e.target));
        entryNodeId = (nodes.find((n) => !targets.has(n.id)) ?? nodes[0]).id;
    }

    // 5. prune unreachable subtrees (orphans left by a removed branch/node).
    if (entryNodeId) {
        const reach = reachableFrom(entryNodeId, edges);
        const keptNodes = nodes.filter((n) => reach.has(n.id));
        const keep = new Set(keptNodes.map((n) => n.id));
        const keptEdges = edges.filter((e) => keep.has(e.source) && keep.has(e.target));
        return { nodes: keptNodes, edges: keptEdges, entryNodeId };
    }

    return { nodes, edges, entryNodeId };
}

/* ─── mutations (each returns a fresh, normalized tree) ─── */

/**
 * Insert `node` into a slot, splicing it ahead of whatever was there:
 * `source -[slot]-> node -> oldTarget`. With `source: null` the node becomes a
 * new root. Used for both insert-after (slot `null`) and add-to-branch (`yes`/`no`).
 */
export function insertNode(tree: SequenceTree, source: string | null, slot: SlotLabel, node: StepNode): SequenceTree {
    if (source === null) {
        // New root ahead of the current entry.
        const edges = [...tree.edges];
        if (tree.entryNodeId) edges.push(makeEdge(node.id, tree.entryNodeId, null));
        return normalizeTree({ nodes: [...tree.nodes, node], edges, entryNodeId: node.id });
    }

    const slotEdge = edgeInSlot(tree.edges, source, slot);
    const oldTarget = slotEdge?.target ?? null;
    const edges = tree.edges.filter((e) => e !== slotEdge);
    edges.push(makeEdge(source, node.id, slot));
    if (oldTarget) edges.push(makeEdge(node.id, oldTarget, null));

    return normalizeTree({ nodes: [...tree.nodes, node], edges, entryNodeId: tree.entryNodeId });
}

/** Add a step of `type` after `afterId` on the linear path. */
export function insertAfter(tree: SequenceTree, afterId: string, type: StepType): SequenceTree {
    return insertNode(tree, afterId, null, makeNode(type));
}

/**
 * Fork the flow after `afterId`. The existing continuation becomes the **No**
 * path (sequence carries on as before unless the condition is met); the **Yes**
 * path gets a fresh step. If `afterId` had no continuation, both sides seed a step.
 */
export function addBranch(tree: SequenceTree, afterId: string): SequenceTree {
    const branch = makeNode('branch');
    const yesLeaf = makeNode('linkedin_message');

    const contEdge = edgeInSlot(tree.edges, afterId, null);
    const oldTarget = contEdge?.target ?? null;

    const edges = tree.edges.filter((e) => e !== contEdge);
    edges.push(makeEdge(afterId, branch.id, null));
    edges.push(makeEdge(branch.id, yesLeaf.id, 'yes'));

    const nodes = [...tree.nodes, branch, yesLeaf];
    if (oldTarget) {
        edges.push(makeEdge(branch.id, oldTarget, 'no'));
    } else {
        const noLeaf = makeNode('email');
        nodes.push(noLeaf);
        edges.push(makeEdge(branch.id, noLeaf.id, 'no'));
    }

    return normalizeTree({ nodes, edges, entryNodeId: tree.entryNodeId });
}

/** Duplicate a non-branch node, splicing the copy in right after the original. */
export function duplicateNode(tree: SequenceTree, id: string): SequenceTree {
    const orig = tree.nodes.find((n) => n.id === id);
    if (!orig || orig.type === 'branch') return tree;
    const copy: StepNode = { ...structuredClone(orig), id: newNodeId(), position: { x: 0, y: 0 } };
    return insertNode(tree, id, null, copy);
}

/**
 * Remove a node and heal the flow: every edge pointing at it is redirected to
 * its primary successor (a non-branch node's continuation, or a branch's No
 * path), and any subtree thereby orphaned is pruned by `normalizeTree`.
 */
export function removeNode(tree: SequenceTree, id: string): SequenceTree {
    const node = tree.nodes.find((n) => n.id === id);
    if (!node) return tree;
    const primary = primarySuccessor(tree, node);

    const edges = tree.edges
        .filter((e) => e.source !== id)
        .map((e) => (e.target === id ? { ...e, target: primary ?? '' } : e))
        .filter((e) => e.target !== '');

    const nodes = tree.nodes.filter((n) => n.id !== id);
    const entryNodeId = tree.entryNodeId === id ? primary : tree.entryNodeId;

    return normalizeTree({ nodes, edges, entryNodeId });
}

/** Replace one node's fields, then re-normalize (a type change can flip its slots). */
export function patchNode(tree: SequenceTree, id: string, patch: Partial<StepNode>): SequenceTree {
    const nodes = tree.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n));
    return normalizeTree({ nodes, edges: tree.edges, entryNodeId: tree.entryNodeId });
}

/* ─── auto-layout (deterministic top-down tidy tree) ─── */

export const STEP_NODE_W = 256;
export const STEP_NODE_H = 108;
export const COND_NODE_W = 232;
export const COND_NODE_H = 96;

// Horizontal gap between sibling subtrees; vertical distance between ranks.
const H_GAP = 64;
const ROW_PITCH = STEP_NODE_H + 84;

function nodeW(node?: StepNode): number {
    return node?.type === 'branch' ? COND_NODE_W : STEP_NODE_W;
}

/**
 * Lay the tree out top-down as a tidy tree. Unlike a generic graph layout this is
 * DETERMINISTIC about branch order — a condition node's **Yes** child is always
 * placed to the LEFT and **No** to the RIGHT — and each subtree is packed into a
 * disjoint horizontal band (leaves advance a shared cursor, parents centre over
 * their children), so Yes/No branches can never cross or overlap (§5).
 *
 * Returns top-left positions keyed by node id (React Flow positions from the
 * top-left corner). Reconvergent / cyclic edges are guarded so a node is laid
 * out exactly once.
 */
export function layoutSequence(nodes: StepNode[], edges: SequenceEdge[]): Map<string, { x: number; y: number }> {
    const pos = new Map<string, { x: number; y: number }>();
    if (!nodes.length) return pos;

    const byId = new Map(nodes.map((n) => [n.id, n]));

    // Ordered children: a branch forks Yes-then-No (fixed left→right); every other
    // node has a single linear continuation.
    const childrenOf = (id: string): string[] => {
        const node = byId.get(id);
        if (!node) return [];
        if (node.type === 'branch') {
            const out: string[] = [];
            const yes = edgeInSlot(edges, id, 'yes')?.target;
            const no = edgeInSlot(edges, id, 'no')?.target;
            if (yes && byId.has(yes)) out.push(yes);
            if (no && byId.has(no) && no !== yes) out.push(no);
            return out;
        }
        const next = edgeInSlot(edges, id, null)?.target;
        return next && byId.has(next) ? [next] : [];
    };

    const targets = new Set(edges.map((e) => e.target));
    const entry = nodes.find((n) => !targets.has(n.id))?.id ?? nodes[0].id;

    const placed = new Set<string>();
    let cursorX = 0;

    // Place a subtree top-down; returns the node's centre x so its parent can
    // centre over it. Leaves consume the next cursor slot; internal nodes sit
    // above the midpoint of their first and last child.
    const walk = (id: string, depth: number): number => {
        if (placed.has(id)) {
            const p = pos.get(id);
            return p ? p.x + nodeW(byId.get(id)) / 2 : cursorX;
        }
        placed.add(id);
        const w = nodeW(byId.get(id));
        const y = depth * ROW_PITCH;

        const kids = childrenOf(id).filter((k) => !placed.has(k));
        if (!kids.length) {
            const x = cursorX;
            cursorX += w + H_GAP;
            pos.set(id, { x, y });
            return x + w / 2;
        }

        const centres = kids.map((k) => walk(k, depth + 1));
        const centre = (centres[0] + centres[centres.length - 1]) / 2;
        pos.set(id, { x: centre - w / 2, y });
        return centre;
    };

    walk(entry, 0);

    // Any node unreachable from entry (a transient orphan mid-edit) — drop it in a
    // spare slot so it's never stacked at the origin.
    for (const n of nodes) {
        if (!pos.has(n.id)) {
            pos.set(n.id, { x: cursorX, y: 0 });
            cursorX += nodeW(n) + H_GAP;
        }
    }

    return pos;
}

/* ─── plain-English summary (§5 "sequence summary" strip) ─── */

function delayClause(node: StepNode, isFirst: boolean): string {
    const v = Number(node.delay?.value) || 0;
    if (v <= 0) return isFirst ? 'immediately' : 'right away';
    const unit = v === 1 ? node.delay.unit.replace(/s$/, '') : node.delay.unit;
    return `after ${v} ${unit}`;
}

function actionPhrase(node: StepNode): string {
    switch (node.type) {
        case 'email':
            return 'email';
        case 'linkedin_invite':
            return 'send a LinkedIn invite';
        case 'linkedin_message':
            return 'send a LinkedIn message';
        case 'linkedin_inmail':
            return 'send a LinkedIn InMail';
        case 'whatsapp_message':
            return 'send a WhatsApp message';
        case 'manual_task':
            return 'create a manual task';
        default:
            return 'continue';
    }
}

/**
 * Render the tree as a short plain-English description. Walks the linear spine;
 * at a branch it inlines "if <condition> <timing>: <yes …>; otherwise <no …>"
 * and stops (the spine has diverged). Visited-guarded against DAG re-convergence.
 */
export function summarizeSequence(tree: SequenceTree): string {
    if (!tree.entryNodeId || !tree.nodes.length) return 'Empty sequence — add a step to begin.';
    const byId = new Map(tree.nodes.map((n) => [n.id, n]));

    const walk = (startId: string | null, depth: number, visited: Set<string>): string[] => {
        const parts: string[] = [];
        let cursor = startId;
        let first = depth === 0;
        while (cursor && byId.has(cursor) && !visited.has(cursor)) {
            visited.add(cursor);
            const node = byId.get(cursor)!;

            if (node.type === 'branch') {
                const cond = node.condition;
                const condText = cond ? CONDITION_LABELS[cond.type] : 'a condition is met';
                const timing =
                    cond?.timing?.mode === 'wait_until'
                        ? ' (wait until it happens)'
                        : ` within ${Math.max(cond?.timing?.windowDays ?? MIN_WITHIN_DAYS, MIN_WITHIN_DAYS)} days`;
                const yes = walk(node.branches.yes, depth + 1, new Set(visited)).join(', ') || 'stop';
                const no = walk(node.branches.no, depth + 1, new Set(visited)).join(', ') || 'stop';
                parts.push(`if the candidate ${condText}${timing} → yes: ${yes}; otherwise: ${no}`);
                break; // the spine forks here
            }

            parts.push(`${actionPhrase(node)} ${delayClause(node, first)}`);
            first = false;
            cursor = edgeInSlot(tree.edges, cursor, null)?.target ?? null;
        }
        return parts;
    };

    const sentence = walk(tree.entryNodeId, 0, new Set()).join(', then ');
    return sentence ? `${sentence[0].toUpperCase()}${sentence.slice(1)}.` : 'Empty sequence — add a step to begin.';
}

/** Count of channel send nodes — the §17 launch blocker mirror for the UI. */
export function countSendNodes(tree: SequenceTree): number {
    return tree.nodes.filter(
        (n) =>
            n.type === 'email' ||
            n.type === 'linkedin_invite' ||
            n.type === 'linkedin_message' ||
            n.type === 'linkedin_inmail' ||
            n.type === 'whatsapp_message'
    ).length;
}
